import type { ReactElement } from "react";
import { Callout, Stack, Text } from "../ui-kit";
import { ActivationMetricWidget } from "./ActivationMetricWidget";
import { AnalyticsStatePanel } from "./AnalyticsStatePanel";
import {
  ANALYTICS_SURFACES,
  type AnalyticsDataSource,
  type AnalyticsSurface,
} from "./analyticsDataSource";
import { CoreEngagementMetricWidget } from "./CoreEngagementMetricWidget";
import styles from "./DashboardView.module.css";
import { DocumentsIndexedChart } from "./DocumentsIndexedChart";
import type { MetricGranularity } from "./fixtures";
import { JourneyFunnel } from "./JourneyFunnel";
import { PeriodSelector } from "./PeriodSelector";
import { RetentionMetricWidget } from "./RetentionMetricWidget";
import { UsersRegisteredChart } from "./UsersRegisteredChart";

const SURFACE_LABELS: Record<AnalyticsSurface, string> = {
  journey: "User journey",
  activation: "Activation",
  retention: "Retention (Day 7)",
  "core-engagement": "Core engagement",
  "users-registered": "New users registered",
  "documents-indexed": "Documents indexed",
};

interface AnalyticsDashboardContentProps {
  readonly source: AnalyticsDataSource;
  readonly granularity: MetricGranularity;
}

function FixtureDashboardContent({
  source,
  granularity,
}: AnalyticsDashboardContentProps): ReactElement {
  return (
    <>
      <Callout className={styles.fixtureCallout} tone="info" title="Test data">
        <Text as="p">
          This dashboard is showing sample data for development and review. It is not live product analytics.
        </Text>
      </Callout>
      <JourneyFunnel />
      <Stack as="section" aria-labelledby="dashboard-metrics-heading" gap={4}>
        <Text as="h2" variant="heading" id="dashboard-metrics-heading">
          Product metrics
        </Text>
        <div className={styles.metricsGrid}>
          <ActivationMetricWidget />
          <RetentionMetricWidget />
          <CoreEngagementMetricWidget />
        </div>
      </Stack>
      <GrowthHeading source={source} granularity={granularity} />
      <div className={styles.chartsGrid}>
        <UsersRegisteredChart granularity={granularity} />
        <DocumentsIndexedChart granularity={granularity} />
      </div>
    </>
  );
}

function GrowthHeading({ source, granularity }: AnalyticsDashboardContentProps): ReactElement {
  const isLoading = source.state.kind === "loading";
  const isNoLiveSource = source.state.kind === "no-live-source";
  const label = isLoading
    ? "Period granularity, updating"
    : isNoLiveSource
      ? "Period granularity, live analytics not available"
      : "Period granularity";
  const disabledReason = isLoading
    ? "Period selection is unavailable while analytics are updating."
    : isNoLiveSource
      ? "Period selection will be available when live analytics are ready."
      : undefined;

  return (
    <Stack as="section" aria-labelledby="dashboard-growth-heading" gap={4}>
      <Stack direction="row" gap={4} align="center" justify="between" wrap>
        <Text as="h2" variant="heading" id="dashboard-growth-heading">
          Growth
        </Text>
        <PeriodSelector
          value={granularity}
          onChange={source.selectPeriod}
          disabled={isLoading || isNoLiveSource}
          busy={isLoading}
          aria-label={label}
          aria-describedby={disabledReason !== undefined ? "dashboard-period-selection-status" : undefined}
        />
      </Stack>
      {disabledReason !== undefined ? (
        <Text as="p" id="dashboard-period-selection-status" variant="caption">
          {disabledReason}
        </Text>
      ) : null}
    </Stack>
  );
}

function SurfacePanel({ source, surface }: { readonly source: AnalyticsDataSource; readonly surface: AnalyticsSurface }): ReactElement {
  const panel = getSurfacePanel(source, surface);
  return (
    <Stack as="section" aria-label={SURFACE_LABELS[surface]} gap={3}>
      <Text as="h3" variant="subheading">{SURFACE_LABELS[surface]}</Text>
      {panel}
    </Stack>
  );
}

function getSurfacePanel(source: AnalyticsDataSource, surface: AnalyticsSurface): ReactElement {
  if (source.state.kind === "live") {
    return <AnalyticsStatePanel state={source.state} surface={surface} />;
  }

  if (source.state.kind === "error" && source.state.dashboard !== undefined) {
    if (source.state.affectedSurfaces?.includes(surface)) {
      return <AnalyticsStatePanel state={source.state} />;
    }
    return <AnalyticsStatePanel state={{ kind: "live", dashboard: source.state.dashboard }} surface={surface} />;
  }

  if (source.state.kind !== "fixture-test-only") {
    return <AnalyticsStatePanel state={source.state} />;
  }
  return <AnalyticsStatePanel state={{ kind: "no-live-source" }} />;
}

function QualifiedSurfacePanels({ source }: Pick<AnalyticsDashboardContentProps, "source">): ReactElement {
  return (
    <>
      <Stack as="section" aria-labelledby="dashboard-journey-heading" gap={4}>
        <Text as="h2" variant="heading" id="dashboard-journey-heading">User journey</Text>
        <SurfacePanel source={source} surface="journey" />
      </Stack>
      <Stack as="section" aria-labelledby="dashboard-metrics-heading" gap={4}>
        <Text as="h2" variant="heading" id="dashboard-metrics-heading">Product metrics</Text>
        <div className={styles.metricsGrid}>
          {ANALYTICS_SURFACES.slice(1, 4).map((surface) => <SurfacePanel key={surface} source={source} surface={surface} />)}
        </div>
      </Stack>
      <div className={styles.chartsGrid}>
        {ANALYTICS_SURFACES.slice(4).map((surface) => <SurfacePanel key={surface} source={source} surface={surface} />)}
      </div>
    </>
  );
}

function DashboardWideState({ source, granularity }: AnalyticsDashboardContentProps): ReactElement {
  return (
    <>
      <Stack as="section" aria-labelledby="dashboard-journey-heading" gap={3}>
        <Text as="h2" variant="heading" id="dashboard-journey-heading">User journey</Text>
      </Stack>
      <Stack as="section" aria-labelledby="dashboard-metrics-heading" gap={3}>
        <Text as="h2" variant="heading" id="dashboard-metrics-heading">Product metrics</Text>
      </Stack>
      <GrowthHeading source={source} granularity={granularity} />
      <Text as="p" variant="caption" role="status">This status applies to User journey, Product metrics, and Growth.</Text>
      <AnalyticsStatePanel state={source.state.kind === "fixture-test-only" ? { kind: "no-live-source" } : source.state} />
    </>
  );
}

export function AnalyticsDashboardContent({ source, granularity }: AnalyticsDashboardContentProps): ReactElement {
  if (source.state.kind === "fixture-test-only") {
    return <FixtureDashboardContent source={source} granularity={granularity} />;
  }

  if (source.state.kind === "live") {
    return (
      <>
      <GrowthHeading source={source} granularity={granularity} />
      <QualifiedSurfacePanels source={source} />
      </>
    );
  }

  if (source.state.kind === "error" && source.state.dashboard !== undefined) {
    return (
      <>
      <GrowthHeading source={source} granularity={granularity} />
      <QualifiedSurfacePanels source={source} />
      </>
    );
  }

  return (
    <DashboardWideState source={source} granularity={granularity} />
  );
}
